import { useEffect, useRef } from 'react';

import { PowerFunnelEvents, platform } from '../Analytics/constants';
import { trackEvent } from '../Analytics/TrackedClick';

/**
 * Fires `power_upgrade_flow_abandoned` on cleanup when the user leaves the upgrade
 * step without calling `markSucceeded()`.
 *
 * Implementation notes:
 *  - Payload values are read from refs at cleanup time so a parent re-render with a
 *    different event / subscription status before cleanup doesn't leak stale values
 *    into the abandonment event.
 *  - `succeededRef` is reset to `false` whenever the user (re-)enters the upgrade
 *    step. Without that, a second visit after a successful completion would never
 *    fire abandonment again.
 */
export const useUpgradeFlowAbandonmentTracking = ({
    isInUpgradeStep,
    userSubscriptionStatus,
    sourceEventId,
    referringSurface,
    gatedFeature,
}) => {
    const succeededRef = useRef(false);
    const userSubscriptionStatusRef = useRef(userSubscriptionStatus);
    const sourceEventIdRef = useRef(sourceEventId);
    const referringSurfaceRef = useRef(referringSurface);
    const gatedFeatureRef = useRef(gatedFeature);

    userSubscriptionStatusRef.current = userSubscriptionStatus;
    sourceEventIdRef.current = sourceEventId;
    referringSurfaceRef.current = referringSurface;
    gatedFeatureRef.current = gatedFeature;

    useEffect(() => {
        if (!isInUpgradeStep) {
            return undefined;
        }
        succeededRef.current = false;
        return () => {
            if (succeededRef.current) {
                return;
            }
            trackEvent(PowerFunnelEvents.POWER_UPGRADE_FLOW_ABANDONED, {
                platform,
                userSubscriptionStatus: userSubscriptionStatusRef.current,
                referringSurface: referringSurfaceRef.current,
                gatedFeature: gatedFeatureRef.current,
                ...(sourceEventIdRef.current && { sourceEventId: sourceEventIdRef.current }),
            });
        };
    }, [isInUpgradeStep]);

    const markSucceeded = () => {
        succeededRef.current = true;
    };

    return { markSucceeded };
};
